import React, {
  forwardRef,
  HTMLAttributes,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import cl from 'classnames';
import styles from './datePickerStyle.module.scss';
import Calendar from './calendar/calendar';
import {
  useCalendarPosition,
  parseDateSingle,
  parseDateRange,
  toDateRange,
  toInputDateRange,
  InputDateRange,
  InputStatus,
  CalendarDate,
  DateRange,
} from './calendar/calendar.utils';
import { useLanguage } from 'src/hooks';
import { Language } from 'src/interface';

type DateInputProps = {
  value: string;
  setValue?: (value: string) => void;
  readOnly?: boolean;
  onInputClick?: () => void;
  onCalendarBtnClick: () => void;
  onClear?: () => void;
  className?: string;
  size?: 'small' | 'medium';
  placeholder?: string;
  withoutClear?: boolean;
  statusStart?: InputStatus;
  statusEnd?: InputStatus;
  activeClnBtn?: boolean;
  btnStyle?: React.CSSProperties;
  disabled?: boolean | null;
};

const DateInputComponent = (
  {
    value,
    setValue,
    readOnly = true,
    onInputClick,
    onCalendarBtnClick,
    onClear,
    className,
    size = 'medium',
    placeholder,
    withoutClear = false,
    statusStart,
    statusEnd = statusStart,
    activeClnBtn = false,
    btnStyle,
    disabled = false,
    ...otherProps
  }: DateInputProps & HTMLAttributes<HTMLInputElement>,
  ref: Ref<HTMLDivElement>,
) => {
  const statusClass = useMemo(() => {
    if (statusStart === 'valid' && statusEnd === 'valid') {
      return styles.valid;
    }

    if (statusStart === 'invalid' && statusEnd === 'invalid') {
      return styles.invalid;
    }

    if (statusStart === 'valid') {
      return styles.validStart;
    }

    if (statusEnd === 'valid') {
      return styles.validEnd;
    }

    return null;
  }, [statusEnd, statusStart]);

  return (
    <div
      className={cl(
        styles.inputContainer,
        size === 'small' && styles.small,
        disabled && styles.disabled,
        className,
      )}
      style={btnStyle}
      ref={ref}
    >
      <div className={cl(styles.inputWrapper, statusClass)}>
        <input
          onClick={(event) => {
            event.stopPropagation();
            onInputClick?.();
          }}
          value={value}
          onChange={(event) => {
            setValue?.(event.target.value);
          }}
          type="text"
          className={cl(
            styles.input,
            size === 'small' && styles.small,
            readOnly && styles.readOnly,
            withoutClear && styles.withoutClear,
          )}
          autoComplete="new-password"
          readOnly={readOnly}
          placeholder={placeholder}
          disabled={Boolean(disabled)}
          {...otherProps}
        />
        {Boolean(value.length) && !withoutClear && (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cl(styles.icon, size === 'small' && styles.small)}
            onClick={(event) => {
              event.stopPropagation();
              onClear?.();
            }}
          >
            <g clipPath="url(#clip0_4966_54804)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="#9EA6BD"
              />
            </g>
            <defs>
              <clipPath id="clip0_4966_54804">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        )}
      </div>
      <div
        className={cl(
          styles.calendarButton,
          activeClnBtn && styles.active,
          activeClnBtn &&
            statusStart === 'valid' &&
            statusEnd === 'valid' &&
            styles.valid,
        )}
        onClick={(event) => {
          event.stopPropagation();
          onCalendarBtnClick?.();
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.dateIcon}
        >
          {activeClnBtn ? (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.78286 2.84967C8.78286 2.38041 8.40245 2 7.93319 2C7.46393 2 7.08352 2.38041 7.08352 2.84967V4.03341H4.88308C4.11844 4.03341 3.38512 4.33716 2.84443 4.87784C2.30375 5.41852 2 6.15185 2 6.91649V19.1169C2 19.8816 2.30375 20.6149 2.84443 21.1556C3.38512 21.6962 4.11844 22 4.88308 22H12.8265C13.2958 22 13.6762 21.6196 13.6762 21.1503C13.6762 20.6811 13.2958 20.3007 12.8265 20.3007H4.88308C4.56913 20.3007 4.26805 20.1759 4.04605 19.9539C3.82406 19.732 3.69935 19.4309 3.69935 19.1169V6.91649C3.69935 6.60254 3.82406 6.30145 4.04605 6.07946C4.26805 5.85747 4.56913 5.73275 4.88308 5.73275H7.08352V6.91649C7.08352 7.38575 7.46393 7.76616 7.93319 7.76616C8.40245 7.76616 8.78286 7.38575 8.78286 6.91649V5.73275H15.2171V6.91649C15.2171 7.38575 15.5975 7.76616 16.0668 7.76616C16.5361 7.76616 16.9165 7.38575 16.9165 6.91649V5.73275H19.1169C19.4309 5.73275 19.732 5.85747 19.9539 6.07946C20.1759 6.30145 20.3007 6.60254 20.3007 6.91649V13.0167V13.1503C20.3007 13.6196 20.6811 14 21.1503 14C21.6196 14 22 13.6196 22 13.1503V6.91649C22 6.15184 21.6962 5.41852 21.1556 4.87784C20.6149 4.33716 19.8816 4.03341 19.1169 4.03341H16.9165V2.84967C16.9165 2.38041 16.5361 2 16.0668 2C15.5975 2 15.2171 2.38041 15.2171 2.84967V4.03341H8.78286V2.84967Z"
                fill="#1F93FF"
              />
              <path
                d="M6.91649 10.1336C6.44722 10.1336 6.06681 10.514 6.06681 10.9833C6.06681 11.4526 6.44722 11.833 6.91649 11.833H17.0835C17.5528 11.833 17.9332 11.4526 17.9332 10.9833C17.9332 10.514 17.5528 10.1336 17.0835 10.1336H6.91649Z"
                fill="#1F93FF"
              />
              {statusStart === 'valid' && statusEnd === 'valid' ? (
                <>
                  <mask id="path-2-inside-1_9670_27136" fill="white">
                    <path d="M23 18.5C23 20.9853 20.9853 23 18.5 23C16.0147 23 14 20.9853 14 18.5C14 16.0147 16.0147 14 18.5 14C20.9853 14 23 16.0147 23 18.5Z" />
                  </mask>
                  <path
                    d="M16.8536 18.1464C16.6583 17.9512 16.3417 17.9512 16.1464 18.1464C15.9512 18.3417 15.9512 18.6583 16.1464 18.8536L16.8536 18.1464ZM20.8536 17.8536C21.0488 17.6583 21.0488 17.3417 20.8536 17.1464C20.6583 16.9512 20.3417 16.9512 20.1464 17.1464L20.8536 17.8536ZM18 20L17.6464 20.3536C17.8417 20.5488 18.1583 20.5488 18.3536 20.3536L18 20ZM16.1464 18.8536L17.6464 20.3536L18.3536 19.6464L16.8536 18.1464L16.1464 18.8536ZM18.3536 20.3536L20.8536 17.8536L20.1464 17.1464L17.6464 19.6464L18.3536 20.3536ZM22 18.5C22 20.433 20.433 22 18.5 22V24C21.5376 24 24 21.5376 24 18.5H22ZM18.5 22C16.567 22 15 20.433 15 18.5H13C13 21.5376 15.4624 24 18.5 24V22ZM15 18.5C15 16.567 16.567 15 18.5 15V13C15.4624 13 13 15.4624 13 18.5H15ZM18.5 15C20.433 15 22 16.567 22 18.5H24C24 15.4624 21.5376 13 18.5 13V15Z"
                    fill="#389E0D"
                    mask="url(#path-2-inside-1_9670_27136)"
                  />
                </>
              ) : (
                <>
                  <circle cx="18.5" cy="18.5" r="4" stroke="#F84544" />
                  <path
                    d="M18.5 17.6187L19.6781 16.4406C19.9215 16.1973 20.316 16.1973 20.5594 16.4406C20.8027 16.684 20.8027 17.0785 20.5594 17.3219L19.3812 18.5L20.5594 19.6781C20.8027 19.9215 20.8027 20.316 20.5594 20.5594C20.316 20.8027 19.9215 20.8027 19.6781 20.5594L18.5 19.3812L17.3219 20.5594C17.0785 20.8027 16.684 20.8027 16.4406 20.5594C16.1973 20.316 16.1973 19.9215 16.4406 19.6781L17.6187 18.5L16.4406 17.3219C16.1973 17.0785 16.1973 16.684 16.4406 16.4406C16.684 16.1973 17.0785 16.1973 17.3219 16.4406L18.5 17.6187Z"
                    fill="#F84544"
                  />
                </>
              )}
            </>
          ) : (
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.93319 2C8.40245 2 8.78286 2.38041 8.78286 2.84967V4.03341H15.2171V2.84967C15.2171 2.38041 15.5976 2 16.0668 2C16.5361 2 16.9165 2.38041 16.9165 2.84967V4.03341H19.1169C19.8816 4.03341 20.6149 4.33716 21.1556 4.87784C21.6962 5.41852 22 6.15184 22 6.91649V19.1169C22 19.8816 21.6962 20.6149 21.1556 21.1556C20.6149 21.6962 19.8816 22 19.1169 22H4.88308C4.11844 22 3.38512 21.6962 2.84443 21.1556C2.30375 20.6149 2 19.8816 2 19.1169V6.91649C2 6.15185 2.30375 5.41852 2.84443 4.87784C3.38512 4.33716 4.11844 4.03341 4.88308 4.03341H7.08352V2.84967C7.08352 2.38041 7.46393 2 7.93319 2ZM7.08352 5.73275H4.88308C4.56913 5.73275 4.26805 5.85747 4.04605 6.07946C3.82406 6.30145 3.69935 6.60254 3.69935 6.91649V19.1169C3.69935 19.4309 3.82406 19.732 4.04605 19.9539C4.26805 20.1759 4.56913 20.3007 4.88308 20.3007H19.1169C19.4309 20.3007 19.732 20.1759 19.9539 19.9539C20.1759 19.732 20.3007 19.4309 20.3007 19.1169V6.91649C20.3007 6.60254 20.1759 6.30145 19.9539 6.07946C19.732 5.85747 19.4309 5.73275 19.1169 5.73275H16.9165V6.91649C16.9165 7.38575 16.5361 7.76616 16.0668 7.76616C15.5976 7.76616 15.2171 7.38575 15.2171 6.91649V5.73275H8.78286V6.91649C8.78286 7.38575 8.40245 7.76616 7.93319 7.76616C7.46393 7.76616 7.08352 7.38575 7.08352 6.91649V5.73275ZM6.06681 10.9833C6.06681 10.514 6.44722 10.1336 6.91649 10.1336H17.0835C17.5528 10.1336 17.9332 10.514 17.9332 10.9833C17.9332 11.4526 17.5528 11.833 17.0835 11.833H6.91649C6.44722 11.833 6.06681 11.4526 6.06681 10.9833Z"
              fill="#1F93FF"
            />
          )}
        </svg>
      </div>
    </div>
  );
};

const DateInput = forwardRef(DateInputComponent);

export const DatePicker: React.FC<{
  selectedDate: CalendarDate | null;
  onChange: (date: CalendarDate | null) => void;
  withoutClear?: boolean;
  inputClassName?: string;
  size?: 'small' | 'medium';
  placeholder?: string;
}> = ({
  selectedDate,
  onChange,
  withoutClear = false,
  inputClassName,
  size,
  placeholder,
}) => {
  const { currentLang } = useLanguage();
  const [inputSelectedDate, setInputSelectedDate] = useState<CalendarDate>(
    selectedDate || new CalendarDate(null),
  );
  const [inputValue, setInputValue] = useState('');

  const {
    isView,
    toHide,
    btnRef,
    portalRef,
    scrollElRef,
    onBtnClick,
    containerPosition,
  } = useCalendarPosition();

  useEffect(() => {
    setInputSelectedDate(selectedDate || new CalendarDate(null));
  }, [selectedDate]);

  useEffect(() => {
    if (inputSelectedDate.isClicked) {
      setInputValue(inputSelectedDate.localString);
    }
  }, [inputSelectedDate, currentLang]);

  useEffect(() => {
    if (isView || inputSelectedDate.isClicked) {
      return;
    }

    if (['valid', null].includes(inputSelectedDate.inputStatus)) {
      const updatedDate = new CalendarDate(inputSelectedDate.date);
      onChange(updatedDate.date ? updatedDate : null);
      setInputSelectedDate(updatedDate);
    } else {
      setInputSelectedDate(selectedDate || new CalendarDate(null));
    }
  }, [inputSelectedDate, onChange, selectedDate, isView]);

  const onSelectDate = useCallback(
    (date: CalendarDate) => {
      setInputSelectedDate(date);
      onChange(date);
      setInputValue(date.localString);
      setTimeout(() => {
        toHide();
      }, 100);
    },
    [onChange, toHide],
  );

  const onClear = useCallback(() => {
    setInputSelectedDate(new CalendarDate(null));
    onChange(null);
    setInputValue('');
    toHide();
  }, [onChange, toHide]);

  const defaultPlaceholder = useMemo(
    () => (currentLang === Language.EN ? 'YYYY-MM-DD' : 'DD.MM.YYYY'),
    [currentLang],
  );

  return (
    <div className={styles.container}>
      <DateInput
        value={inputValue}
        onInputClick={onBtnClick}
        onCalendarBtnClick={onBtnClick}
        ref={btnRef}
        onClear={onClear}
        withoutClear={withoutClear}
        className={inputClassName}
        size={size}
        placeholder={placeholder || defaultPlaceholder}
        readOnly
        statusStart={inputSelectedDate.inputStatus}
        btnStyle={{
          visibility: containerPosition ? 'hidden' : 'visible',
        }}
      />
      {containerPosition &&
        createPortal(
          <div className={styles.portal} onClick={toHide} ref={portalRef}>
            <div className={styles.scrollEl} ref={scrollElRef} />
            <div
              className={styles.calendarContainer}
              onClick={(event) => {
                event.stopPropagation();
              }}
              style={{
                minWidth:
                  containerPosition.width <= 276
                    ? 286
                    : containerPosition.width + 10,
                maxWidth:
                  containerPosition.width <= 276
                    ? 286
                    : containerPosition.width + 10,
                top: containerPosition.top,
                left: containerPosition.left,
              }}
            >
              <DateInput
                value={inputValue}
                setValue={(value) => {
                  setInputValue(value);
                  setInputSelectedDate(parseDateSingle(value, withoutClear));
                }}
                onCalendarBtnClick={toHide}
                onClear={onClear}
                withoutClear={withoutClear}
                className={inputClassName}
                btnStyle={{
                  margin: 0,
                  width: containerPosition.width,
                }}
                size={size}
                placeholder={placeholder || defaultPlaceholder}
                readOnly={false}
                statusStart={inputSelectedDate.inputStatus}
                activeClnBtn
              />
              <Calendar
                show={isView}
                selectedDate={inputSelectedDate}
                onSelectDate={onSelectDate}
                className={styles.calendar}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export const DateRangePicker: React.FC<{
  range: DateRange;
  onChange: (range: DateRange) => void;
  withoutClear?: boolean;
  inputClassName?: string;
  size?: 'small' | 'medium';
  placeholder?: string;
  disabled?: boolean | null;
}> = ({
  range,
  onChange,
  withoutClear = false,
  inputClassName,
  size = 'medium',
  placeholder,
  disabled = false,
}) => {
  const { currentLang } = useLanguage();
  const [inputRange, setInputRange] = useState<InputDateRange>(
    toInputDateRange(range),
  );
  const [inputValue, setInputValue] = useState<string>(
    inputRange.start
      ? `${inputRange.start.localString} - ${inputRange.end?.localString}`
      : '',
  );
  const {
    isView,
    toHide,
    btnRef,
    portalRef,
    scrollElRef,
    onBtnClick,
    containerPosition,
  } = useCalendarPosition();

  useEffect(() => {
    setInputRange(toInputDateRange(range));
  }, [range]);

  useEffect(() => {
    if (!inputRange.start.isClicked && !inputRange.end.isClicked) {
      return;
    }

    let value = inputRange.start.localString;

    if (inputRange.end && value) {
      value += ` - ${inputRange.end.localString}`;
    }

    setInputValue(value);
  }, [inputRange.start, inputRange.end, currentLang]);

  useEffect(() => {
    if (isView) {
      return;
    }

    const startValid = ['valid', null].includes(inputRange.start.inputStatus);
    const endValid = ['valid', null].includes(inputRange.end.inputStatus);
    const updatedRange = toDateRange(inputRange);

    if (inputRange.start.isClicked && inputRange.end.isClicked) {
      if (!inputRange.start.date || !inputRange.end.date) {
        const newInputRange = toInputDateRange(range);
        if (
          newInputRange.start.isEqual(inputRange.start) &&
          newInputRange.end.isEqual(inputRange.end) &&
          newInputRange.reverse === inputRange.reverse
        ) {
          return;
        }
        setInputRange(toInputDateRange(range));
      }
      return;
    }

    if (
      Boolean(inputRange.start.date) !== Boolean(inputRange.end.date) ||
      !startValid ||
      !endValid
    ) {
      setInputRange(toInputDateRange(range));
      return;
    }

    onChange(updatedRange);
    setInputRange(toInputDateRange(updatedRange));
  }, [range, inputRange.start, inputRange.end, onChange, isView, inputRange]);

  const onSelectDateRange = useCallback(
    (dateRange: DateRange) => {
      setInputRange(toInputDateRange(dateRange));

      if (dateRange.start?.date && dateRange.end?.date) {
        onChange({
          start: !dateRange.reverse ? dateRange.start : dateRange.end,
          end: !dateRange.reverse ? dateRange.end : dateRange.start,
        });
        setTimeout(() => {
          toHide();
        }, 100);
      }
    },
    [onChange, toHide],
  );

  const onClear = useCallback(() => {
    setInputRange({
      start: new CalendarDate(null),
      end: new CalendarDate(null),
    });
    onChange({ start: null, end: null });
    setInputValue('');
    toHide();
  }, [onChange, toHide]);

  const setValue = useCallback(
    (value: string) => {
      setInputValue(value);
      setInputRange((prevState) =>
        parseDateRange(value, prevState, withoutClear),
      );
    },
    [withoutClear],
  );

  const defaultPlaceholder = useMemo(
    () =>
      currentLang === Language.EN
        ? 'YYYY-MM-DD - YYYY-MM-DD'
        : 'DD.MM.YYYY - DD.MM.YYYY',
    [currentLang],
  );

  return (
    <div className={styles.container}>
      <DateInput
        value={inputValue}
        onInputClick={() => {
          if (disabled) {
            return;
          }

          onBtnClick();
        }}
        onCalendarBtnClick={() => {
          if (disabled) {
            return;
          }

          onBtnClick();
        }}
        ref={btnRef}
        onClear={() => {
          if (disabled) {
            return;
          }

          onClear();
        }}
        withoutClear={withoutClear}
        className={inputClassName}
        size={size}
        placeholder={placeholder || defaultPlaceholder}
        readOnly
        statusStart={inputRange.start?.inputStatus}
        statusEnd={inputRange.end?.inputStatus}
        btnStyle={{
          visibility: containerPosition ? 'hidden' : 'visible',
        }}
        disabled={disabled}
      />
      {containerPosition &&
        createPortal(
          <div className={styles.portal} onClick={toHide} ref={portalRef}>
            <div className={styles.scrollEl} ref={scrollElRef} />
            <div
              className={styles.calendarContainer}
              onClick={(event) => {
                event.stopPropagation();
              }}
              style={{
                minWidth:
                  containerPosition.width <= 276
                    ? 286
                    : containerPosition.width + 10,
                maxWidth:
                  containerPosition.width <= 276
                    ? 286
                    : containerPosition.width + 10,
                top: containerPosition.top,
                left: containerPosition.left,
              }}
            >
              <DateInput
                value={inputValue}
                setValue={setValue}
                onCalendarBtnClick={toHide}
                onClear={onClear}
                withoutClear={withoutClear}
                className={inputClassName}
                btnStyle={{
                  margin: 0,
                  width: containerPosition.width,
                }}
                size={size}
                placeholder={placeholder || defaultPlaceholder}
                readOnly={false}
                statusStart={inputRange.start?.inputStatus}
                statusEnd={inputRange.end?.inputStatus}
                activeClnBtn
              />
              <Calendar
                show={isView}
                isRange
                selectedDateRange={inputRange}
                onSelectDateRange={onSelectDateRange}
                className={styles.calendar}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
